import { useState } from 'react'
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline'
import { Form, Button } from 'shared/components'
import { useTranslation } from 'libs/i18n'
import { useForm } from 'libs/form'
import { format } from 'libs/dates'
import { useAxiosPrivate, useUserRoles } from 'shared/hooks'
import { useNavigate, useParams, useSearchParams } from 'libs/router'
import { useMutation, useQueryClient } from 'libs/query'
import { queryKeys } from 'config/queryKeys'
import { apiRoutes } from 'config/apiRoutes'
import { routes } from 'config/routes'
import { useBookingData } from '../../hooks'
import { usePopulateBooking } from './hooks/usePopulateBooking'
import { BookingRes } from 'features/bookings/types/bookings.types'
import { useMarkAsConfirmed } from '../../mutations/useMarkAsConfirmed'
import { useBookingOptions } from '../../../../hooks'
import { BookingFormSections } from '../../../../organisms/BookingForm/BookingFormSections'
import { ImportProductsModal } from '../ProductsTable/ImportProductsModal'

type BookingFormProps = {
	booking: BookingRes | undefined
}

const outlineButton =
	'h-9 rounded-md border border-border bg-background px-4 text-sm font-medium text-foreground shadow hover:bg-muted'

export const BookingForm = ({ booking }: BookingFormProps) => {
	const { t } = useTranslation()
	const axiosPrivate = useAxiosPrivate()
	const params = useParams()
	const queryClient = useQueryClient()
	const [searchParams] = useSearchParams()
	const navigate = useNavigate()
	const { isUser } = useUserRoles()
	const { markAsConfirmedMutation, isMarkAsConfirmedPending } = useMarkAsConfirmed({
		id: booking?.data.id
	})
	const form = useForm()
	const { handleSubmit, reset } = form
	const { editBookingSuccessMsg, editBookingFailMsg } = useBookingData()
	const options = useBookingOptions({ daysLabel: t('days') })

	usePopulateBooking({
		data: booking?.data,
		rentals: options.rentals.items,
		reset
	})

	const { mutate: editBookingMutation, isPending: editBookingLoading } = useMutation({
		mutationFn: (data: any) => axiosPrivate.patch(apiRoutes.booking(params.id as string), data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all })
			editBookingSuccessMsg()
			navigate(
				searchParams.toString()
					? `${routes.bookingIndex}?${searchParams.toString()}`
					: routes.bookingIndex
			)
		},
		onError: (err: any) => {
			const data = err.response.data
			const nonFieldErrors = 'non_field_errors' in data ? data.non_field_errors : []
			const catchAllErrors = '__all__' in data ? data.__all__ : []
			const allErrors = [...nonFieldErrors, ...catchAllErrors]
			editBookingFailMsg(allErrors.join('\n'))()
		}
	})

	const onSubmit = (data: any) => {
		if (isNotesOnly) {
			editBookingMutation({ store_notes: data.store_notes })
			return
		}

		editBookingMutation({
			user_id: data.user?.value,
			rental: data.rental?.value,
			start_date: format(data.from, 'yyyy-MM-dd'),
			end_date: format(data.to, 'yyyy-MM-dd'),
			commission: data.commission,
			payment: {
				status: data.payment.status.value,
				method: data.payment.method.value
			},
			booth_id: data.booth?.value,
			store_notes: data.store_notes
		})
	}

	const handleReopen = () => {
		if (typeof booking?.data.id === 'number') markAsConfirmedMutation(booking?.data.id)
	}

	const isBookingDisabled = ['CANCELLED', 'COMPLETED', 'OVERDUE', 'ABANDONED'].includes(
		booking?.data?.status as string
	)
	const isCancelled = booking?.data?.status === 'CANCELLED'
	const isNotesOnly = !isUser && isBookingDisabled
	const bookingId = params.id
	const isImportEnabled = !!bookingId && !!booking?.data?.product_import_enabled
	const [isImportModalOpen, setImportModalOpen] = useState(false)

	return (
		<Form
			variant="sections"
			className="max-w-none"
			submitText={isNotesOnly ? t('Save notes') : t('Save changes')}
			onSubmit={handleSubmit(onSubmit)}
			isLoading={editBookingLoading}
			disabled={isUser}
			startActions={
				isImportEnabled && (
					<Button
						styleType="outline"
						className={`${outlineButton} w-full whitespace-nowrap sm:w-auto [&_svg]:h-4 [&_svg]:w-4`}
						onClick={() => setImportModalOpen(true)}>
						<ArrowUpTrayIcon />
						{t('Import products')}
					</Button>
				)
			}>
			<BookingFormSections
				mode="edit"
				form={form}
				options={options}
				showUser={!isUser}
				showNotes={!isUser}
				disabled={isUser || isBookingDisabled}
				notesDisabled={isUser}
				notesAside={
					isCancelled && !isUser ? (
						<div>
							<Button
								styleType="outline"
								className={outlineButton}
								isLoading={isMarkAsConfirmedPending}
								onClick={handleReopen}>
								{t('Reopen Booking')}
							</Button>
						</div>
					) : undefined
				}
			/>
			{isImportEnabled && (
				<ImportProductsModal
					isOpened={isImportModalOpen}
					closeModal={() => setImportModalOpen(false)}
					bookingId={bookingId}
				/>
			)}
		</Form>
	)
}
